import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { productPath } from '@/lib/constants'
import type { Product } from '@/lib/products'
import ProductVisual from './ProductVisual'
import { PlatformChips, ProductIconTile, ProductPrimaryAction, StatusBadge } from './ProductBits'

/**
 * Product card used on Home and /products. Answers, at a glance:
 * what is it, who is it for, is it available, and where do I go next.
 */
export default function ProductCard({
  product,
  featured = false,
  dimmed = false,
  className,
}: {
  product: Product
  featured?: boolean
  dimmed?: boolean
  className?: string
}) {
  const { t } = useTranslation(['products', 'common'])
  const href = productPath(product.slug)

  return (
    <article
      className={cn(
        'group relative flex min-w-0 flex-col overflow-hidden rounded-3xl border bg-card shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift',
        featured && 'lg:flex-row',
        dimmed && 'opacity-40 saturate-50 hover:opacity-100 hover:saturate-100',
        className
      )}
    >
      {/* Visual */}
      <Link
        to={href}
        tabIndex={-1}
        aria-hidden
        className={cn(
          'relative flex items-center justify-center overflow-hidden',
          product.accent.soft,
          featured ? 'p-5 sm:p-8 lg:order-last lg:w-[55%]' : 'px-4 pb-4 pt-5 sm:px-6 sm:pt-6'
        )}
      >
        <div className="bg-grid absolute inset-0 opacity-60" aria-hidden />
        <ProductVisual
          product={product}
          variant="card"
          className={cn(
            'w-full transition-transform duration-500 group-hover:scale-[1.02]',
            featured ? 'max-w-xl' : 'max-w-sm'
          )}
        />
      </Link>

      {/* Body */}
      <div className={cn('flex min-w-0 flex-1 flex-col gap-4 p-5 sm:p-6', featured && 'lg:justify-center lg:p-10')}>
        <div className="flex items-start gap-3">
          <ProductIconTile product={product} size={featured ? 'lg' : 'md'} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className={cn('font-bold leading-tight', featured ? 'text-2xl lg:text-3xl' : 'text-xl')}>
                <Link to={href} className="focus-visible:outline-none focus-visible:underline">
                  {t(`${product.id}.name`)}
                </Link>
              </h3>
              <StatusBadge status={product.status} className="shrink-0" />
            </div>
            <p className={cn('mt-0.5 text-sm font-medium', product.accent.text)}>{t(`${product.id}.category`)}</p>
          </div>
        </div>

        <p className={cn('leading-relaxed text-muted-foreground', featured ? 'text-base sm:text-lg' : 'text-[15px]')}>
          {t(`${product.id}.tagline`)}
        </p>

        {featured && (
          <ul className="grid grid-cols-1 gap-2">
            {(t(`${product.id}.highlights`, { returnObjects: true }) as string[]).map((h) => (
              <li key={h} className="flex items-start gap-2 text-sm">
                <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', product.accent.solid)} />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        <PlatformChips platforms={product.platforms} />

        <div className="mt-auto grid grid-cols-1 gap-2 pt-2 sm:flex sm:flex-wrap sm:items-center">
          <Link
            to={href}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-foreground px-5 text-sm font-medium text-background transition-opacity hover:opacity-90"
          >
            {t('common:actions.learnMore')}
            <ArrowRight className="h-4 w-4 flip-rtl transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
          </Link>
          <ProductPrimaryAction product={product} variant="outline" className="sm:border-transparent sm:bg-transparent" />
        </div>
      </div>
    </article>
  )
}
